import { memo, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@doger/ui/components/ui/dialog';
import { Link } from '@doger/ui/components/ui/link';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@doger/ui/components/ui/tabs';
import { GoogleAuth, SignInForm, SignUpForm } from '@doger/core/modules/auth';
import { JwtDecoder } from '@doger/core/modules/auth/jwt';

import {
  LogAmplitudeEventOnMount,
  useAnalytics,
} from '@doger/chat/shared/analytics';

import { useAuth } from './auth.context';
import styles from './auth-modal.module.scss';

export const DEFAULT_AUTH_TYPE = 'signin';

export const AuthModal = memo(() => {
  const {
    isAuthModalOpen,
    setIsAuthModalOpen,
    authType,
    setAuthType,
    setIsEmailConfirmationModalOpen,
    setIsWhitelistModalOpen,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const onSuccess = useCallback(
    (tokens: { accessToken: string; refreshToken: string }) => {
      setIsAuthModalOpen(false);

      const result = JwtDecoder.decodeAccessToken(tokens.accessToken);

      if (result.success) {
        if (!result.data.isConfirmed) {
          setIsEmailConfirmationModalOpen(true);

          return;
        }

        if (!result.data.completedProfile) {
          setIsWhitelistModalOpen(true);

          return;
        }
      }

      if (location.state?.from) {
        navigate(location.state.from);
      }
    },
    [location, navigate, setIsAuthModalOpen]
  );

  const onLocalSignUpSuccess = useCallback(() => {
    analytics.amplitude.track({
      event: 'User Signed Up',
      properties: {
        source: 'local',
      },
    });

    setIsAuthModalOpen(false);
    setIsEmailConfirmationModalOpen(true);
  }, [setIsAuthModalOpen, setIsEmailConfirmationModalOpen]);

  const onGoogleSuccess = useCallback(
    (response: {
      accessToken: string;
      refreshToken: string;
      isExistingUser: boolean;
    }) => {
      if (!response.isExistingUser) {
        analytics.amplitude.track({
          event: 'User Signed Up',
          properties: {
            source: 'google',
            googleAuthType: 'button',
          },
        });
      }

      onSuccess(response);
    },
    [onSuccess]
  );

  const handleClose = useCallback(() => {
    setIsAuthModalOpen(false);
  }, [setIsAuthModalOpen]);

  return (
    <Dialog open={isAuthModalOpen} onOpenChange={handleClose}>
      <DialogContent>
        <LogAmplitudeEventOnMount event='Sign In Modal Viewed' />

        <DialogHeader className={styles.header}>
          <DialogTitle>{t('onboarding.welcome.title')}</DialogTitle>

          <DialogDescription>
            {t('onboarding.welcome.subtitle2')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <GoogleAuth onSuccess={onGoogleSuccess} />

          <div className={styles.divider}>
            <div className={styles.dividerLine} />
            <span className={styles.dividerText}>
              {t('auth.orContinueWith')}
            </span>
            <div className={styles.dividerLine} />
          </div>

          <Tabs
            value={authType ?? DEFAULT_AUTH_TYPE}
            onValueChange={setAuthType}
          >
            <TabsList>
              <TabsTrigger value='signin'>{t('auth.signIn')}</TabsTrigger>
              <TabsTrigger value='signup'>{t('auth.signUp')}</TabsTrigger>
            </TabsList>

            <TabsContent value='signin'>
              <SignInForm className={styles.form} onSuccess={onSuccess} />
            </TabsContent>

            <TabsContent value='signup'>
              <SignUpForm
                className={styles.form}
                onSuccess={onLocalSignUpSuccess}
              />
            </TabsContent>
          </Tabs>

          <p className={styles.footerText}>
            <Trans
              t={t}
              i18nKey='auth.footerText'
              components={{
                tos: (
                  <Link
                    to='/terms-of-service'
                    palette='secondary'
                    target='_blank'
                  />
                ),
                pp: (
                  <Link
                    to='/privacy-policy'
                    palette='secondary'
                    target='_blank'
                  />
                ),
              }}
            />
          </p>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
});
